import styled, { keyframes } from 'styled-components';
import { colors, radii, spacing, typography } from '../../../../theme-v2';
import { AddonLayout } from '../AddonLayout';
import type { TrackKey } from './types';

/**
 * Card background gradients, per MED-7075's mockup. Podcast keeps its own
 * green identity distinct from the app's teal accent; AudioDoc reuses the
 * accent tokens so it stays on-brand. Both settle into the same near-black
 * at the bottom — not yet a theme-v2 token, so it's a literal here.
 */
const CARD_DARK = '#111827';
const GRADIENT: Record<TrackKey, string> = {
  podcast: `linear-gradient(160deg, #10B981 0%, #047857 55%, ${CARD_DARK} 100%)`,
  audioDoc: `linear-gradient(160deg, ${colors.accent.base} 0%, ${colors.accent.active} 55%, ${CARD_DARK} 100%)`,
};

/** Plain wrapper for the loading/empty/error states — before a track is
 *  playing there's nothing to theme, so this stays the neutral surface
 *  rather than guessing at the gradient. */
export const StyledWrapper = styled(AddonLayout)`
  gap: ${spacing.md}px;
  padding: 24px 32px;
  background: ${colors.surface.base};
  border-radius: ${radii.box}px;
`;

/**
 * Full-bleed gradient card. Built on `AddonLayout` (a flex column) so the
 * content centers both ways and STAYS centered — horizontally when the
 * Knowledge Hub panel is widened (e.g. chat collapsed on Podcast play), and
 * vertically when the card has more height than the content needs, rather
 * than pinning everything to the top with dead space below.
 */
export const StyledPlayerCard = styled(AddonLayout).attrs({
  align: 'center',
  justify: 'center',
  gap: spacing.md,
})<{ $trackKey: TrackKey }>`
  padding: 40px ${spacing.lg}px;
  background: ${({ $trackKey }) => GRADIENT[$trackKey]};
  border-radius: ${radii.box}px;
  text-align: center;
`;

export const StyledCover = styled.img`
  width: 100%;
  height: 100%;
  border-radius: ${radii.box}px;
  object-fit: cover;
`;

export const StyledCoverFrame = styled.div`
  width: 140px;
  height: 140px;
  border-radius: ${radii.box}px;
  background: rgba(255, 255, 255, 0.12);
  color: ${colors.text.onAccent};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const wavePulse = keyframes`
  0%, 100% { transform: scaleY(0.4); }
  50% { transform: scaleY(1); }
`;

/** Animated "audio is playing" indicator — bars pulse independently so the
 *  whole shape doesn't move in lockstep. Shared by Podcast and AudioDoc: the
 *  static paused icon is what tells the two variants apart (see TrackPlayer). */
export const StyledWave = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 48px;

  span {
    display: block;
    width: 8px;
    height: 100%;
    border-radius: ${radii.full}px;
    background: ${colors.text.onAccent};
    animation: ${wavePulse} 1.1s ease-in-out infinite;

    &:nth-child(1) {
      animation-delay: -0.9s;
    }
    &:nth-child(2) {
      animation-delay: -0.6s;
    }
    &:nth-child(3) {
      animation-delay: -0.3s;
    }
    &:nth-child(4) {
      animation-delay: 0s;
    }
  }
`;

export const StyledTitle = styled.span`
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.bold};
  line-height: ${typography.lineHeight.tight};
  color: ${colors.text.onAccent};
`;

export const StyledSubtitle = styled.span<{ $muted?: boolean }>`
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.sm}px;
  color: rgba(255, 255, 255, ${({ $muted }) => ($muted ? 0.55 : 0.7)});
`;

export const StyledTimeLabel = styled.span`
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.xs}px;
  color: rgba(255, 255, 255, 0.55);
`;

export const StyledRateButton = styled.button<{ $active?: boolean }>`
  padding: 2px 10px;
  border-radius: ${radii.full}px;
  border: 1px solid
    ${({ $active }) =>
      $active ? colors.text.onAccent : 'rgba(255, 255, 255, 0.3)'};
  background: ${({ $active }) =>
    $active ? colors.text.onAccent : 'rgba(255, 255, 255, 0.12)'};
  color: ${({ $active }) => ($active ? CARD_DARK : colors.text.onAccent)};
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.medium};
  cursor: pointer;

  &:hover {
    border-color: ${colors.text.onAccent};
  }
`;

/**
 * Central play/pause circle. Not `IconButton`: its size steps fix the icon
 * at 16/24/32px (no 22px option) and its hover state is `!important`, which
 * would repaint this white circle teal on hover — neither is what the
 * mockup wants, so this is a small dedicated button instead.
 */
export const StyledPlayButton = styled.button`
  width: 56px;
  height: 56px;
  border-radius: ${radii.full}px;
  border: none;
  background: ${colors.text.onAccent};
  color: ${colors.text.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: ${colors.text.onAccent};
  }
`;

export const StyledSkipButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.7);

  &:hover {
    color: ${colors.text.onAccent};
  }

  span {
    font-family: ${typography.fontFamily};
    font-size: ${typography.size.xs}px;
    font-weight: ${typography.weight.medium};
  }
`;

export const StyledMessage = styled.div`
  padding: ${spacing.base}px;
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.md}px;
  color: ${colors.text.secondary};
  text-align: center;
`;

export const StyledShimmerCover = styled.div`
  width: 64px;
  height: 64px;
  border-radius: ${radii.box}px;
  background: ${colors.surface.muted};
  flex-shrink: 0;
`;

export const StyledShimmerLine = styled.div<{ $w: string; $h?: number }>`
  height: ${({ $h }) => $h ?? 14}px;
  width: ${({ $w }) => $w};
  border-radius: ${radii.xs}px;
  background: ${colors.surface.muted};
`;
